import React from 'react';
import moment from 'moment';
import { Oval } from 'react-loader-spinner';
import { BsPlus, BsPencil, BsTrash, BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getPunchCardsForDriverV2 } from '../../../DriversV2/driversV2Slice';
import {
  setShowCreatePunchCardModal,
  setPunchCardToUpdate,
  deletePunchCard,
  getCurrentPayPeriod,
  getPayPeriodById,
  getPayPeriodSummaries
} from '../../../PunchCards/punchCardSlice';
import { PunchCard, getWorkTime, getWorkTimeForDriver } from '../../../../models';

import './PunchCardsTab.scss';

export interface PunchCardsTabProps {
  userId: string;
  driverName: string;
}

export function PunchCardsTab(props: PunchCardsTabProps) {
  const { userId, driverName } = props;
  const dispatch = useAppDispatch();

  const punchCardsForDriver = useAppSelector(state => state.driversV2.punchCardsForDriver);
  const getPunchCardsAPI = useAppSelector(state => state.driversV2.getPunchCardsForDriver);
  const createPunchCardAPI = useAppSelector(state => state.punchCards.createPunchCardAPI);
  const updatePunchCardAPI = useAppSelector(state => state.punchCards.updatePunchCardAPI);
  const deletePunchCardAPI = useAppSelector(state => state.punchCards.deletePunchCardAPI);
  const currentPayPeriod = useAppSelector(state => state.punchCards.currentPayPeriod);
  const getPayPeriodAPI = useAppSelector(state => state.punchCards.getPayPeriodAPI);
  const payPeriodSummaries = useAppSelector(state => state.punchCards.payPeriodSummaries);

  // Get this driver's pay period summary
  const driverSummary = payPeriodSummaries?.find(s => s.driverId === userId);

  // Fetch current pay period on mount
  React.useEffect(() => {
    dispatch(getCurrentPayPeriod());
  }, []);

  // Fetch punch cards and summaries when pay period changes
  React.useEffect(() => {
    if (currentPayPeriod) {
      dispatch(getPunchCardsForDriverV2({
        driverId: userId,
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [userId, currentPayPeriod?.payPeriod.id]);

  // Refetch punch cards and summaries when created, updated, or deleted
  React.useEffect(() => {
    if (!createPunchCardAPI.loading && !!createPunchCardAPI.value && currentPayPeriod) {
      dispatch(getPunchCardsForDriverV2({
        driverId: userId,
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [createPunchCardAPI.loading, createPunchCardAPI.value]);

  React.useEffect(() => {
    if (!updatePunchCardAPI.loading && !!updatePunchCardAPI.value && currentPayPeriod) {
      dispatch(getPunchCardsForDriverV2({
        driverId: userId,
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [updatePunchCardAPI.loading, updatePunchCardAPI.value]);

  React.useEffect(() => {
    if (!deletePunchCardAPI.loading && !!deletePunchCardAPI.value && currentPayPeriod) {
      dispatch(getPunchCardsForDriverV2({
        driverId: userId,
        fromDate: currentPayPeriod.payPeriod.startDate,
        toDate: currentPayPeriod.payPeriod.endDate
      }));
      dispatch(getPayPeriodSummaries(currentPayPeriod.payPeriod.id));
    }
  }, [deletePunchCardAPI.loading, deletePunchCardAPI.value]);

  const status = punchCardsForDriver?.summary.onClock ? 'On clock' : 'Not on clock';

  const onPreviousPayPeriod = () => {
    if (currentPayPeriod?.payPeriod.previousId) {
      dispatch(getPayPeriodById(currentPayPeriod.payPeriod.previousId));
    }
  };

  const onNextPayPeriod = () => {
    if (currentPayPeriod?.payPeriod.nextId) {
      dispatch(getPayPeriodById(currentPayPeriod.payPeriod.nextId));
    }
  };

  const onAddPunchCardClick = () => {
    dispatch(setShowCreatePunchCardModal({ driverId: userId }));
  };

  const onEditPunchCardClick = (punchCard: PunchCard) => {
    dispatch(setPunchCardToUpdate(punchCard));
  };

  const onDeletePunchCardClick = (punchCard: PunchCard) => {
    if (window.confirm('Are you sure you want to delete this punch card?')) {
      dispatch(deletePunchCard(punchCard.id));
    }
  };

  const formatPayPeriodDates = () => {
    if (!currentPayPeriod) return '';
    const start = moment(currentPayPeriod.payPeriod.startDate);
    const end = moment(currentPayPeriod.payPeriod.endDate);
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  };

  const formatDecimalHours = (minutes: number) => `${(minutes / 60).toFixed(2)} hrs`;

  const renderSummaryCard = () => (
    <div className='summary-card'>
      <div className='summary-header'>
        <span className='summary-title'>Summary</span>
      </div>
      <div className='summary-divider' />
      <div className='summary-content'>
        <div className='summary-row'>
          <span className='row-label'>Driver</span>
          <span className='row-value'>{driverName}</span>
        </div>
        <div className='summary-row'>
          <span className='row-label'>Status</span>
          <span className={`row-value ${punchCardsForDriver?.summary.onClock ? 'on-clock' : ''}`}>
            {status}
          </span>
        </div>
        <div className='summary-row'>
          <span className='row-label'>Total time</span>
          <span className='row-value'>{getWorkTimeForDriver(punchCardsForDriver)}</span>
        </div>
        {driverSummary && (
          <>
            <div className='summary-row'>
              <span className='row-label'>Regular time</span>
              <span className='row-value'>{formatDecimalHours(driverSummary.totalRegularMinutes)}</span>
            </div>
            <div className='summary-row'>
              <span className='row-label'>Overtime</span>
              <span className={`row-value ${driverSummary.totalOvertimeMinutes > 0 ? 'overtime' : ''}`}>
                {formatDecimalHours(driverSummary.totalOvertimeMinutes)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderPunchCard = (punchCard: PunchCard) => (
    <div key={punchCard.id} className='punch-card-item'>
      <div className='punch-card-row'>
        <span className='row-label'>Date</span>
        <span className='row-value'>{punchCard.date}</span>
      </div>
      <div className='punch-card-row'>
        <span className='row-label'>Clock in</span>
        <span className='row-value'>{moment(punchCard.punchIn).format('LT')}</span>
      </div>
      <div className='punch-card-row'>
        <span className='row-label'>Clock out</span>
        <span className='row-value'>{punchCard.punchOut ? moment(punchCard.punchOut).format('LT') : 'NA'}</span>
      </div>
      <div className='punch-card-row'>
        <span className='row-label'>Working time</span>
        <span className='row-value'>{getWorkTime(punchCard)}</span>
      </div>
      <div className='punch-card-actions'>
        <div className='action-button edit' onClick={() => onEditPunchCardClick(punchCard)}>
          <BsPencil />
          Edit
        </div>
        <div className='action-button delete' onClick={() => onDeletePunchCardClick(punchCard)}>
          <BsTrash />
          Delete
        </div>
      </div>
    </div>
  );

  const isLoading = getPunchCardsAPI.loading || getPayPeriodAPI.loading;

  return (
    <div className='punch-cards-tab'>
      <div className='pay-period-picker'>
        <div
          className={`nav-button ${!currentPayPeriod?.payPeriod.previousId ? 'disabled' : ''}`}
          onClick={onPreviousPayPeriod}
        >
          <BsChevronLeft />
        </div>
        <div className='pay-period-dates'>
          {formatPayPeriodDates()}
        </div>
        <div
          className={`nav-button ${!currentPayPeriod?.payPeriod.nextId ? 'disabled' : ''}`}
          onClick={onNextPayPeriod}
        >
          <BsChevronRight />
        </div>
        {isLoading && (
          <div className='loading-indicator'>
            <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/>
          </div>
        )}
        <div className='add-button' onClick={onAddPunchCardClick}>
          <BsPlus className='icon' />
          Add Punch Card
        </div>
      </div>

      {renderSummaryCard()}

      <div className='punch-cards-section'>
        <div className='section-title'>Punch Cards</div>

        {!isLoading && punchCardsForDriver?.punchCards.length === 0 && (
          <div className='empty-state'>No punch cards for this period.</div>
        )}

        <div className='punch-cards-list'>
          {punchCardsForDriver?.punchCards
            .slice()
            .sort((a, b) => moment(b.punchIn).valueOf() - moment(a.punchIn).valueOf())
            .map(renderPunchCard)}
        </div>
      </div>
    </div>
  );
}
